import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Award, GraduationCap, Sparkles, Wind } from "lucide-react";
import { Layout } from "../components/Layout";
import { LotusMark } from "../components/Logo";
import { siteConfig } from "../data/siteConfig";

const qualifications = [
  "Chartered Accountant",
  "Residential Yoga Instructor",
  "Yoga Wellness Instructor (Govt. of India)",
  "Kids Yoga Instructor",
];

const certifications = [
  {
    year: "2018",
    title: "Yoga Instructor Course (4-week residential)",
    org: "Rikhiapeeth Ashram — Satyananda / Bihar School of Yoga",
  },
  {
    year: "2023",
    title: "21-day Better Living Course",
    org: "The Yoga Institute, Mumbai",
  },
  {
    year: "2024",
    title: "RCYS Kids Teacher Training",
    org: "Bodhi School of Yoga",
  },
  {
    year: "2024",
    title: "Kids Teacher Training",
    org: "Yoga Vidya Gurukul",
  },
  {
    year: "2025",
    title: "YCB Level-2 Yoga Wellness Instructor",
    org: "Ministry of Ayush, Govt. of India",
  },
  {
    year: "2025",
    title: "100-hour Kids Yoga Teacher Training",
    org: "International Yoga Academy / Yoga Alliance USA",
  },
];

const specialisations = [
  "Kids Yoga",
  "Prenatal Yoga",
  "Disease-Specific Yoga",
  "Yoga Workshops",
  "Residential Yoga",
];

const practices = [
  "Mantra Chanting",
  "Asanas",
  "Yogic Breathing — Kapalbhati & Pranayama",
  "Meditation & Relaxation",
];

export const AboutPage = () => {
  return (
    <Layout>
      {/* Intro */}
      <section className="pt-28 md:pt-32 pb-16">
        <div className="container mx-auto px-6 grid md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-2">
            <div className="rounded-[2.5rem] overflow-hidden shadow-soft-lg border-4 border-white max-w-sm mx-auto">
              <img
                src="/gallery/suman-portrait.jpg"
                alt="Suman Agrawal"
                className="w-full h-[26rem] object-cover object-top"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest mb-5">
              <LotusMark className="w-4 h-4" /> About
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-semibold text-charcoal mb-4">
              Suman Agrawal
            </h1>
            <p className="text-primary font-semibold mb-6">
              Chartered Accountant &amp; Yoga Instructor
            </p>
            <div className="space-y-4 text-charcoal/75 leading-relaxed">
              <p>
                Trained at Rikhiapeeth and affiliated with the Bihar School of
                Yoga, Suman has lived yoga as a way of life since childhood,
                rooted in a spiritually grounded upbringing.
              </p>
              <p>
                With 5+ years of teaching online and offline across India and
                Hong Kong, she conducts sessions that create harmony between
                body, mind and emotions — for individuals and groups alike — and
                runs yoga workshops in schools, colleges and organisations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission quote */}
      <section className="py-14 bg-charcoal text-cream">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <LotusMark className="w-10 h-10 mx-auto mb-6" stroke="#FBF8F1" />
          <p className="font-heading text-2xl md:text-3xl italic leading-relaxed">
            “{siteConfig.mission}”
          </p>
        </div>
      </section>

      {/* Qualifications + Specialisations */}
      <section className="py-20">
        <div className="container mx-auto px-6 grid md:grid-cols-2 gap-8">
          <div className="bg-white rounded-3xl p-8 border border-accent/15 shadow-soft">
            <span className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-5">
              <Award className="w-6 h-6" />
            </span>
            <h2 className="font-heading text-2xl text-charcoal mb-5">
              Qualifications
            </h2>
            <ul className="space-y-3">
              {qualifications.map((q) => (
                <li key={q} className="flex items-start gap-3 text-charcoal/75">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                  {q}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-3xl p-8 border border-accent/15 shadow-soft">
            <span className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-5">
              <Sparkles className="w-6 h-6" />
            </span>
            <h2 className="font-heading text-2xl text-charcoal mb-5">
              Specialisations
            </h2>
            <div className="flex flex-wrap gap-2.5">
              {specialisations.map((s) => (
                <span
                  key={s}
                  className="px-4 py-2 rounded-full bg-cream-dark text-charcoal/80 text-sm font-medium border border-accent/20"
                >
                  {s}
                </span>
              ))}
            </div>

            <h3 className="font-heading text-xl text-charcoal mt-8 mb-4 flex items-center gap-2">
              <Wind className="w-5 h-5 text-primary" /> Yogic practices offered
            </h3>
            <ul className="space-y-2">
              {practices.map((p) => (
                <li key={p} className="flex items-start gap-3 text-charcoal/75">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Certifications timeline */}
      <section className="py-16 bg-cream-dark">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-12">
            <span className="w-12 h-12 rounded-2xl bg-primary/10 text-primary inline-flex items-center justify-center mb-4">
              <GraduationCap className="w-6 h-6" />
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-charcoal">
              Certifications &amp; Training
            </h2>
          </div>
          <div className="space-y-4">
            {certifications.map((c, i) => (
              <div
                key={i}
                className="flex gap-5 bg-white rounded-2xl p-5 border border-accent/15 shadow-soft"
              >
                <div className="shrink-0">
                  <span className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary text-white font-heading text-xl font-semibold">
                    {c.year}
                  </span>
                </div>
                <div>
                  <h3 className="font-heading text-lg text-charcoal">
                    {c.title}
                  </h3>
                  <p className="text-sm text-charcoal/65">{c.org}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 text-center">
        <div className="container mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-4xl text-charcoal mb-4">
            Practice with Suman
          </h2>
          <p className="text-charcoal/70 mb-8 max-w-xl mx-auto">
            Explore the programs or reach out to find the right class for you.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <RouterLink
              to="/programs"
              className="px-7 py-3.5 bg-primary text-white rounded-full font-semibold shadow-soft hover:bg-primary-dark transition-all"
            >
              View Programs
            </RouterLink>
            <RouterLink
              to="/contact"
              className="px-7 py-3.5 bg-white text-primary border border-accent/40 rounded-full font-semibold hover:bg-cream-dark transition-all"
            >
              Contact
            </RouterLink>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
